import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { readYowanAuthAdapter, syncYowanAuthFoundation } from './yowanAuthAdapter';

const YowanAuthContext = createContext(null);

export const YowanAuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(() => readYowanAuthAdapter());
  const currentUser = auth.currentUser;

  const refreshAuth = useCallback(() => {
    const nextAuth = readYowanAuthAdapter();
    setAuth(nextAuth);
    return nextAuth;
  }, []);

  const syncAuth = useCallback(async () => {
    const nextAuth = await syncYowanAuthFoundation();
    setAuth(nextAuth);
    return nextAuth;
  }, []);

  useEffect(() => {
    const needsAuthSync = currentUser.isAuthenticated && !currentUser.email;
    const needsAdminSync =
      currentUser.isAuthenticated &&
      !currentUser.canAccessAdmin &&
      !auth.accessMe &&
      !auth.adminStatus;

    if (!needsAuthSync && !needsAdminSync) {
      return undefined;
    }

    let active = true;

    syncYowanAuthFoundation()
      .then((nextAuth) => {
        if (active) {
          setAuth(nextAuth);
        }
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [
    auth.accessMe,
    auth.adminStatus,
    currentUser,
    currentUser.canAccessAdmin,
    currentUser.email,
    currentUser.isAuthenticated,
  ]);

  const value = useMemo(
    () => ({
      auth,
      currentUser,
      refreshAuth,
      syncAuth,
      setAuth,
    }),
    [auth, currentUser, refreshAuth, syncAuth]
  );

  return <YowanAuthContext.Provider value={value}>{children}</YowanAuthContext.Provider>;
};

export const useYowanAuth = () => {
  const context = useContext(YowanAuthContext);

  if (!context) {
    throw new Error('useYowanAuth must be used within YowanAuthProvider');
  }

  return context;
};
